/**
 * Shape Category Registry — picker plugin point.
 *
 * Lets a domain self-register its shape-picker tab from its
 * `register<Domain>Domain()` call at boot, instead of hand-editing
 * the central `shapeCategories` map in ShapePickerTool.tsx.
 *
 * Exists alongside the existing hardcoded categories — ShapePickerTool
 * merges both at render time. Existing 7 categories (genogram, gender,
 * basic, flowchart, bpmn, org, network) stay hardcoded for now;
 * follow-up issue tracks migrating them. New verticals (software-design,
 * planning, future strategy storybook) MUST use this registry.
 *
 * See plans/software-design/playbook.md § X (App affordance wiring) and
 * plans/strategy-planning/playbook.md § X for the contract.
 */

import type { DiagramType } from './registries';
import type { ComponentType, FC, SVGProps } from 'react';

/** Single shape entry in a category (matches the existing inline shape). */
export interface RegisteredShapeEntry {
  /** Shape id — must match a registered manifest in DiagramTypeRegistry. */
  id: string;
  /** Display label in the picker. */
  label: string;
  /** Lucide icon component, or any FC<{ className?: string }>. */
  icon?: ComponentType<{ className?: string }> | FC<SVGProps<SVGSVGElement>>;
  /** Variant tag (used by tools like the swimlane-vertical override). */
  variant?: string;
  /** Preview key — when set, ShapePickerTool renders a domain preview component. */
  preview?: string;
  /** Routes execute through a different category if set (e.g., gender shapes execute as genogram). */
  executeCategory?: string;
  /** Default text for shapes that need pre-filled labels. */
  defaultText?: string;
}

/** Connector option (e.g., line) attached to a category. */
export interface RegisteredConnectorEntry {
  id: string;
  label: string;
  icon: ComponentType<{ className?: string }>;
  variant?: string;
}

/** A self-registered category. */
export interface RegisteredShapeCategory {
  /** Category id — used as the picker tab key. Must be unique. */
  id: string;
  /** Tab label shown to the user. */
  name: string;
  /** Shapes in this category. */
  shapes?: RegisteredShapeEntry[];
  /** Optional connector options (rare — most categories have none). */
  connectors?: RegisteredConnectorEntry[];
  /** Which `DiagramType`s this category should appear under. */
  applicableDiagramTypes: DiagramType[];
  /** Display order vs other categories on the same diagram (lower = earlier). Default 100. */
  order?: number;
}

const registry = new Map<string, RegisteredShapeCategory>();

/**
 * Register a shape-picker category. Idempotent — calling twice with the
 * same id replaces the prior registration. Domain `register<Domain>Domain()`
 * functions call this once at boot.
 */
export function registerShapeCategory(category: RegisteredShapeCategory): void {
  registry.set(category.id, category);
}

/** Read all registered categories sorted by `order` ascending. */
export function listRegisteredShapeCategories(): RegisteredShapeCategory[] {
  return Array.from(registry.values()).sort(
    (a, b) => (a.order ?? 100) - (b.order ?? 100),
  );
}

/** Get a single category by id. */
export function getRegisteredShapeCategory(id: string): RegisteredShapeCategory | undefined {
  return registry.get(id);
}

/**
 * For a given DiagramType, return the registered category ids that should
 * appear in the picker (in order). ShapePickerTool merges these with its
 * hardcoded `diagramTypeCategoryMapping` for the legacy 7 categories.
 */
export function getRegisteredCategoryIdsForDiagramType(
  diagramType: DiagramType,
): string[] {
  return listRegisteredShapeCategories()
    .filter((c) => c.applicableDiagramTypes.includes(diagramType))
    .map((c) => c.id);
}
